<template>
  <!-- The look and feel is the Actions tab's, deliberately: same header shape, same
       lifecycle tabs, same filter pills, same list-row anatomy, same tokens. A promo
       and an action are the same kind of thing to a retailer — something you did on
       purpose and want measured — so arriving at one from the other should feel like
       the same room.

       What differs is the lifecycle. An action is open / in progress / concluded; a
       promo runs, then waits on its sales figures, then has a number. That middle
       stage is promo-specific, and it is where the work is. -->
  <div class="promos-wrap pro">
    <!-- Promotions is a PAGE, not a tab inside one, so it keeps the page heading every
         other page uses — <Section> and its title. Only the CONTENT borrows the Actions
         tab's design: the lifecycle tabs, the filter pills, the row anatomy, the
         tokens. Mimicking the Actions page HEADER here would have given this page a
         second kind of title, one no other page has. -->
    <Section :title="$t('promos.title')">
      <div class="pro-top">
        <p class="pro-sub">{{ $t('promos.sub') }}</p>
        <button v-if="canEdit" type="button" class="btn-primary-sm" @click="showForm = !showForm">
          <i class="bi" :class="showForm ? 'bi-x-lg' : 'bi-plus-lg'"></i>
          {{ showForm ? $t('promos.common.close') : $t('promos.list.logPromo') }}
        </button>
      </div>

      <p v-if="!showForm && canEdit" class="pro-intro">
        {{ $t('promos.list.intro') }}
      </p>

      <!-- ====================== THE YEAR AT A GLANCE ======================
           Above the tabs and outside them on purpose: it spans every stage, so
           burying it in one of them (or behind a disclosure) made it answer a
           narrower question than it actually answers. The cumulative total sits
           beside it, where the eye already is. -->
      <div class="overview">
        <div class="ov-cal">
          <div class="ov-head">{{ $t('promos.list.pastYear') }}</div>
          <div class="cal">
            <div v-for="z in zonesWithPromos" :key="z" class="cal-row">
              <div class="cal-zone">{{ z === STORE_ROW ? $t('promos.common.wholeStore') : z }}</div>
              <div class="cal-track">
                <div
                  v-for="p in promosFor(z)" :key="p.PromoId"
                  class="cal-band verdict-chip"
                  :class="p.measurement.phase === 'running' ? 'v-early' : archetypeClass(archKey(p))"
                  :style="bandStyle(p)"
                  :title="`${p.Data.name} · ${fmtRange(p.StartDate, p.EndDate)}`"
                  @click="open(p)"
                ></div>
              </div>
            </div>
            <!-- the axis is a cal-row too, so it is aligned by the same grid as the
                 tracks rather than by a hardcoded margin that has to be kept in step -->
            <div class="cal-row cal-axis-row">
              <div aria-hidden="true"></div>
              <div class="cal-axis">
                <span v-for="t in axisTicks" :key="t.key"
                      :class="t.edge ? 'axis-edge axis-' + t.edge : 'axis-mid'"
                      :style="t.edge ? null : { left: t.left }">{{ t.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="roll" class="pro-earned">
          <span class="pro-earned-label"><i class="bi bi-tag"></i> {{ $t('promos.list.madeBy') }}</span>
          <span class="pro-earned-value" :class="{ 'is-neg': roll.total < 0 }">
            {{ eur(roll.total) }}
          </span>
          <span class="pro-earned-note">{{ $t('promos.list.measuredNote', { n: roll.count }) }}</span>
          <span class="pro-earned-split">
            <template v-if="roll.wins">{{ $t('promos.list.madeMoney', { n: roll.wins }) }}</template>
            <template v-if="roll.wins && roll.losses"> · </template>
            <template v-if="roll.losses">{{ $t('promos.list.lostMoney', { n: roll.losses }) }}</template>
          </span>
        </div>
      </div>

    <!-- ===================== Lifecycle tabs ===================== -->
    <nav class="stage-tabs" role="tablist" :aria-label="$t('promos.list.lifecycleAria')">
      <template v-for="(st, i) in stages" :key="st.key">
        <span v-if="i" class="stage-arrow" aria-hidden="true"><i class="bi bi-chevron-right"></i></span>
        <button type="button" role="tab" class="stage-tab"
                :class="{ active: stage === st.key }"
                :aria-selected="stage === st.key" @click="stage = st.key">
          <span class="stage-count">{{ st.count }}</span>
          <span class="stage-text">
            <span class="stage-label">{{ st.label }}</span>
            <span class="stage-hint">{{ st.hint }}</span>
          </span>
        </button>
      </template>
    </nav>

    <!-- One panel, one list. The stage decides what goes in it, so the row markup
         exists once rather than three times. -->
    <section class="stage" role="tabpanel" :aria-label="currentStage.label">
      <!-- The stage that has no equivalent in Actions, and the one that earns its own
           tab: the promo is over, the cameras have done their part, and the only thing
           between the store and a euro is three numbers off a till report. -->
      <div v-if="stage === 'waiting' && byStage.waiting.length" class="notice">
        <i class="bi bi-receipt notice-ic" aria-hidden="true"></i>
        <div class="notice-body">
          <strong>{{ $t('promos.list.waitingNotice', { n: byStage.waiting.length },
                       byStage.waiting.length) }}</strong>
          {{ $t('promos.list.waitingBody') }}
        </div>
      </div>

      <div v-if="stage === 'measured'" class="toolbar">
        <div class="filters" role="group" :aria-label="$t('promos.list.filterAria')">
          <button v-for="f in resultFilters" :key="f.key" type="button"
                  class="filter" :class="['st-' + f.key, { active: resultFilter === f.key }]"
                  :disabled="f.key !== 'all' && !f.count"
                  :aria-pressed="resultFilter === f.key"
                  @click="resultFilter = f.key">
            {{ f.label }} <span class="filter-n">{{ f.count }}</span>
          </button>
        </div>
      </div>

      <!-- ============================ FORM ============================ -->
      <!-- Grouped in the order the manager knows the answers: what it was, what the
           customer got, what it was worth, and anything we could never see. Fifteen
           fields in one flat grid is a wall; four short groups is a form. -->
      <div v-if="showForm" class="form-card">

        <!-- ① identity: what you are logging -->
        <div class="fgroup">
          <div class="fgroup-head">{{ $t('promos.form.thePromo') }}</div>
          <div class="form-grid">
            <label class="fld fld-wide"><span>{{ $t('promos.form.name') }}</span>
              <input type="text" v-model="draft.name" :placeholder="$t('promos.form.namePlaceholder')" />
            </label>
            <label class="fld"><span>{{ $t('promos.form.where') }}</span>
              <select v-model="draft.zone">
                <!-- Store-wide first, because a customer day or a store event is not
                     attached to a shelf and a manager looking for it should not have
                     to scan a list of aisles to discover it is possible. -->
                <option :value="STORE_SCOPE">{{ $t('promos.form.wholeStore') }}</option>
                <option v-for="z in zones" :key="z" :value="z">{{ z }}</option>
              </select>
              <!-- Said at the moment of choosing, not discovered in the result. A
                   counting line measures the money and the traffic perfectly well;
                   it is only the stop-rate it can never see. -->
              <small v-if="countsOnlyZones.includes(draft.zone)" class="hint">
                {{ $t('promos.form.countsOnlyHint') }}
              </small>
            </label>
            <label class="fld">
              <span>{{ $t('promos.form.firstDay') }}</span>
              <DateField v-model="draft.start" :min="minDate" :aria-label="$t('promos.form.firstDayAria')" />
            </label>
            <label class="fld">
              <span>{{ $t('promos.form.lastDay') }}</span>
              <DateField v-model="draft.end" :min="draft.start || minDate" :aria-label="$t('promos.form.lastDayAria')" />
            </label>
            <!-- Coverage asks how much of a ZONE the promoted items are. Store-wide
                 there are no promoted items and no zone, so the question does not
                 apply and is not asked. -->
            <label v-if="!storeWide" class="fld fld-wide"><span>{{ $t('promos.form.coverage') }}</span>
              <select v-model="draft.coverage">
                <option v-for="c in COVERAGE_OPTIONS" :key="c.key" :value="c.key">{{ c.label }}</option>
              </select>
            </label>
          </div>
        </div>

        <!-- ② the items. A promo is rarely one product at one rate — 35% off the
             hero line and 30% off the rest is ordinary — and an average of
             PERCENTAGES across different prices is the wrong average. So each item
             carries its own three numbers and we average the MONEY. -->
        <div v-if="!storeWide" class="fgroup">
          <div class="fgroup-head">{{ $t('promos.form.onPromo') }}</div>
          <table class="items">
            <thead>
              <tr>
                <th>{{ $t('promos.form.item') }}</th>
                <th>{{ $t('promos.form.normalPrice') }}</th>
                <th>{{ $t('promos.form.marginPct') }}</th>
                <th>{{ $t('promos.form.discountPct') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, i) in draft.items" :key="i">
                <td :data-label="$t('promos.form.item')"><input type="text" v-model="it.name" :placeholder="$t('promos.form.itemPlaceholder')" /></td>
                <td :data-label="$t('promos.form.normalPrice')"><input type="number" min="0" step="0.01" v-model.number="it.price" /></td>
                <td :data-label="$t('promos.form.marginPct')"><input type="number" min="0" max="100" v-model.number="it.marginPct" /></td>
                <td :data-label="$t('promos.form.discountPct')"><input type="number" min="0" max="100" v-model.number="it.discountPct" /></td>
                <td>
                  <button v-if="draft.items.length > 1" type="button" class="row-x"
                          @click="draft.items.splice(i, 1)" :aria-label="$t('promos.form.removeItem')">×</button>
                </td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="row-add" @click="addItem">{{ $t('promos.form.addItem') }}</button>

          <!-- the derived figures, shown back so an average that looks wrong is
               caught here rather than in the result -->
          <p v-if="itemSummary" class="items-sum">
            {{ $t('promos.form.acrossItems', { n: itemSummary.items }, itemSummary.items) }}
            <b>{{ eur2(itemSummary.unitMarginNormal) }}</b> {{ $t('promos.form.keptNormal') }}
            <b>{{ eur2(itemSummary.unitMarginOnPromo) }}</b> {{ $t('promos.form.keptPromo') }}
            <span v-if="itemSummary.items > 1" class="items-note">
              {{ $t('promos.form.itemsNote', { n: itemSummary.items }) }}
            </span>
          </p>
        </div>

        <!-- ③ the offer: what the customer actually got. One column, so the
             checkbox sits under its dropdown rather than beside it. -->
        <div class="fgroup">
          <div class="fgroup-head">{{ $t('promos.form.customerGot') }}</div>
          <div class="form-stack">
            <label class="fld"><span>{{ $t('promos.form.kind') }}</span>
              <select v-model="draft.mechanic">
                <option v-for="m in MECHANICS" :key="m.k" :value="m.k">{{ m.label }}</option>
              </select>
            </label>
            <label class="fld chk">
              <div class="chk-line">
                <input type="checkbox" v-model="draft.advertised" />
                <span>{{ $t('promos.form.advertised') }}</span>
              </div>
            </label>
          </div>
        </div>

        <!-- ④ what it cost you to put on -->
        <div class="fgroup">
          <div class="fgroup-head">{{ $t('promos.form.costAndSold') }}</div>
          <div class="form-grid">
            <label class="fld"><span>{{ $t('promos.form.setupCost') }}</span>
              <input type="number" min="0" v-model.number="draft.fixedCostEur" />
              <!-- Gifts go HERE, not in a per-customer field. You buy 1,500 of them
                   whether 1,200 or 1,800 people turn up, so the cost is what you
                   bought — a sunk one you know, not a per-head one you cannot. -->
              <small class="hint">
                <template v-if="storeWide">
                  {{ $t('promos.form.setupHintStore') }}
                </template>
                <template v-else>{{ $t('promos.form.setupHint') }}</template>
              </small>
            </label>
          </div>

          <!-- Each stretch opens the day it finishes, so a running promo already
               takes its before figure instead of making the store come back for
               everything at the end. -->
          <div v-if="salesAsk" class="sales-ask">
            <p class="sales-ask-why">
              <template v-if="storeWide">
                {{ $t('promos.common.readPosStore') }}
              </template>
              <template v-else>{{ $t('promos.common.readPos') }}</template>
            </p>
            <table class="sales-ask-t">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ storeWide ? $t('promos.common.transactions') : $t('promos.common.unitsSold') }}</th>
                  <th>{{ $t('promos.common.revenue') }}</th>
                  <!-- A whole store's cost of goods for four particular days is not a
                       figure anyone has to hand, so store-wide it is one average
                       margin asked once below, not a column asked three times. -->
                  <th v-if="!storeWide">
                    {{ $t('promos.common.goodsCost') }}<small>{{ $t('promos.common.goodsCostSmall') }}</small>
                  </th>
                  <th class="col-gross">{{ $t('promos.common.grossMarginEur') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in salesAsk" :key="row.key">
                  <td>
                    <b>{{ row.label }}</b>
                    <span class="sales-when">{{ row.when }}</span>
                  </td>
                  <td :data-label="storeWide ? $t('promos.common.transactions') : $t('promos.common.unitsSold')">
                    <input type="number" min="0" :disabled="row.pending"
                             v-model.number="draft.salesPeriods[row.key].units" /></td>
                  <td :data-label="$t('promos.common.revenue')">
                    <input type="number" min="0" :disabled="row.pending"
                             v-model.number="draft.salesPeriods[row.key].revenue" /></td>
                  <td v-if="!storeWide" :data-label="$t('promos.common.goodsCost')"><input type="number" min="0" :disabled="row.pending"
                             v-model.number="draft.salesPeriods[row.key].cost" /></td>
                  <!-- not an input: it is the subtraction, shown so the store can see
                       where the euro on the result comes from -->
                  <td class="col-gross" :data-label="$t('promos.common.grossMarginEur')">
                    <span v-if="grossOf(row) !== null" class="gross-val">
                      {{ num(grossOf(row)) }}
                    </span>
                    <span v-else class="gross-na">—</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <label v-if="storeWide" class="store-margin">
              <span>{{ $t('promos.common.averageMargin') }}</span>
              <input type="number" min="1" max="99" step="0.5"
                     v-model.number="draft.storeMarginPct" />
              <small class="hint">
                {{ $t('promos.form.storeMarginHint') }}
              </small>
            </label>

            <p v-if="costMissing" class="sales-ask-warn">
              <i class="bi bi-exclamation-triangle"></i>
              {{ $t('promos.form.costMissing') }}
            </p>
          </div>
          <p v-if="salesNote" class="sales-later">{{ salesNote }}</p>
        </div>

        <!-- ④ things we could never see for ourselves -->
        <div class="fgroup">
          <div class="fgroup-head">{{ $t('promos.form.anythingElse') }}</div>
          <div class="form-grid">
            <label v-if="alreadyRan" class="fld fld-wide chk">
              <div class="chk-line">
                <input type="checkbox" v-model="draft.soldOut" />
                <span>{{ $t('promos.form.soldOut') }}</span>
              </div>
              <small class="hint">{{ $t('promos.form.soldOutHint') }}</small>
            </label>
            <label class="fld fld-wide">
              <input type="text" v-model="draft.notes"
:placeholder="$t('promos.form.notesPlaceholder')" />
            </label>
          </div>
        </div>

        <!-- What you said last time you promoted in this zone. This is the moment the
             judgement is worth something — you are deciding whether to do it again. -->
        <div v-if="prior" class="prior" :class="'v-' + (prior.tone === 'good' ? 'improved' : prior.tone === 'bad' ? 'declined' : 'flat')">
          <i class="bi bi-clock-history"></i>
          <div>
            <p class="prior-line">{{ prior.line }}</p>
            <p v-if="prior.note" class="prior-note">“{{ prior.note }}”</p>
            <button type="button" class="prior-link" @click="openById(prior.promoId)">
              {{ $t('promos.form.seeWhatItDid') }}
            </button>
          </div>
        </div>

        <!-- ===================== READINESS =====================
             Feedback on the form, not a separate screen: it says which half of the
             answer this window can actually give, BEFORE any dates are committed. -->
        <!-- `v-if` because the live portal FETCHES this: until a zone and both dates
             are in there is nothing to ask the server about, and a request in flight
             or one that failed leaves it null. The demo computes it locally and never
             sees either state. -->
        <div v-if="readiness" class="readiness">
          <div class="rd-head"><i class="bi bi-speedometer2"></i> {{ $t('promos.readiness.head') }}</div>
          <dl class="rd-list">
            <div class="rd-row">
              <dt>{{ $t('promos.readiness.comparedAgainst') }}</dt>
              <dd>
                {{ $t('promos.readiness.normalDays', { n: readiness.baseline.days }) }}
                <span class="rd-mode">{{ readiness.baseline.mode === 'matched-weekday'
                  ? $t('promos.readiness.sameWeekdays') : '' }}</span>
              </dd>
            </div>
            <div class="rd-row">
              <dt>{{ $t('promos.readiness.footfall') }}</dt>
              <dd>
                {{ $t('promos.readiness.footfallBefore') }}
                <b>{{ rawPct(readiness.detectability.camera.mdePct) }}%</b> {{ $t('promos.readiness.footfallAfter') }}
                <template v-if="readiness.coverage && readiness.coverage.footfallIsHardToRead">
                  {{ $t('promos.readiness.coverage', { label: readiness.coverage.label }) }}
                </template>
              </dd>
            </div>
            <div class="rd-row">
              <dt>{{ $t('promos.readiness.sales') }}</dt>
              <dd v-if="draft.catPromo">
                {{ $t('promos.readiness.salesBefore') }} <b>{{ rawPct(readiness.detectability.sales.mdePct) }}%</b>
                {{ $t('promos.readiness.salesAfter') }}
              </dd>
              <dd v-else>
                {{ $t('promos.readiness.noSales') }}
              </dd>
            </div>
            <div class="rd-row">
              <dt>{{ $t('promos.readiness.breakEven') }}</dt>
              <dd :class="{ danger: readiness.breakEvenLift === null }">
                {{ breakEvenPreview }}
              </dd>
            </div>
          </dl>
          <p v-for="r in readiness.risks" :key="r.kind" class="rd-risk">
            <i class="bi bi-exclamation-triangle"></i> {{ r.text }}
          </p>

        </div>
        <p v-else-if="draft.zone && draft.start && draft.end" class="rd-waiting">
          {{ $t('promos.readiness.working') }}
        </p>

        <!-- Same convention as the sales figures on the verdict page: the button
             wakes up when there is something to save, and says plainly that the demo
             does not keep it. A permanently grey Save with the reason hidden in a
             `title` leaves the reader wondering what they did wrong. -->
        <div class="form-actions">
          <button type="button" class="btn-primary" :disabled="!canLog" @click="onLog">
            {{ $t('promos.common.save') }}
          </button>
          <button type="button" class="btn-ghost" @click="showForm = false">{{ $t('promos.common.cancel') }}</button>
          <span v-if="!canLog" class="form-hint">{{ $t('promos.form.needNameDates') }}</span>
        </div>
        <p v-if="loggedNote" class="demo-note">
          <i class="bi bi-info-circle"></i>
          {{ $t('promos.form.demoNote') }}
        </p>
      </div>
      <p v-if="!visibleRows.length" class="empty-state">{{ currentStage.empty }}</p>

      <ul v-else class="promo-list">
        <li v-for="p in visibleRows" :key="p.PromoId" class="promo" :class="'kind-' + kindOf(p)">
          <button type="button" class="promo-row" @click="open(p)">
            <!-- left: what it was -->
            <span class="promo-id">
              <span class="meta">
                <span class="dot" aria-hidden="true"></span>
                <span class="meta-kind">{{ kindLabel(p) }}</span>
                <span class="meta-sep" aria-hidden="true">·</span>
                <span class="meta-zone">{{ p.Data.scope === 'store' ? $t('promos.common.wholeStore') : p.Data.zoneName }}</span>
                <span class="meta-date">{{ fmtRange(p.StartDate, p.EndDate) }}</span>
                <span v-if="!hasMoney(p) && hasEnded(p)"
                      class="meta-tag tag-nomoney">
                  <i class="bi bi-receipt"></i> {{ $t('promos.list.noSales') }}
                </span>
                <span v-if="repeatOf(p)" class="meta-tag" :class="repeatClass(p.Data.outcome.repeat)">
                  {{ repeatOf(p).chip }}
                </span>
                <!-- A measured promo with no answer looks identical to one nobody has
                     asked about. The figures are only half of it: whether you would
                     run it again is the half we cannot work out, and an absent chip
                     asks for nothing. -->
                <span v-else-if="needsVerdict(p)" class="meta-tag tag-ask">
                  <i class="bi bi-question-circle"></i> {{ $t('promos.list.wouldRepeat') }}
                </span>
                <!-- the only thing on this page that can still change an outcome
                     rather than explain one, so it survives the move to a row -->
                <span v-if="p.live && p.live.alarms.length" class="meta-tag tag-alarm">
                  <i class="bi bi-exclamation-triangle-fill"></i>
                  {{ $t('promos.list.toCheck', { n: p.live.alarms.length }, p.live.alarms.length) }}
                </span>
              </span>
              <span class="promo-what">
                {{ p.Data.name }}<template v-if="p.Data.productScope"> — {{ p.Data.productScope }}</template>
              </span>
            </span>

            <!-- middle: the money, or why there is none. "gross margin" alone could
                 be the during figure, the after figure or the lot, so what it covers
                 is one hover away rather than a second line on every row. -->
            <Tooltip v-if="hasMoney(p)" :text="moneyScope(p)" placement="top" align="end">
              <span class="promo-reading">
                <span class="promo-reading-label">{{ $t('promos.list.grossMargin') }} <i class="bi bi-info-circle"></i></span>
                <span class="promo-reading-value"
                      :class="p.measurement.margin.eur < 0 ? 'rd-down' : 'rd-up'">
                  {{ eur(p.measurement.margin.eur) }}
                </span>
              </span>
            </Tooltip>
            <span v-else class="promo-reading">
              <span class="promo-reading-label">{{ $t('promos.list.grossMargin') }}</span>
              <span class="promo-reading-value rd-pending">{{ $t('promos.list.notInYet') }}</span>
            </span>

            <!-- right: where it is in its life -->
            <span class="promo-status">
              <template v-if="p.measurement.phase === 'running'">
                <span class="progress" :aria-label="$t('promos.list.dayOfAria', { d: ranDays(p), n: runLength(p) })">
                  <span class="progress-fill" :style="{ width: runPct(p) + '%' }"></span>
                </span>
                <span class="promo-status-note">{{ $t('promos.list.dayOf', { d: ranDays(p), n: runLength(p) }) }}</span>
              </template>
              <template v-else-if="unmeasurable(p)">
                <span class="verdict-chip v-flat">{{ $t('promos.list.cantMeasure') }}</span>
                <span class="promo-status-note">{{ $t('promos.list.noHistory') }}</span>
              </template>
              <template v-else-if="tooEarly(p)">
                <span class="verdict-chip v-early">{{ $t('promos.list.tooEarly') }}</span>
              </template>
              <template v-else>
                <span v-if="duringOfP(p)" class="verdict-chip" :class="TONE[duringOfP(p).tone]">
                  {{ duringOfP(p).label }}
                </span>
                <span class="promo-status-note">
                  <template v-if="afterOfP(p)">{{ $t('promos.list.then', { after: afterOfP(p).label.toLowerCase() }) }}</template>
                  <template v-else>{{ $t('promos.list.afterNotIn') }}</template>
                </span>
              </template>
            </span>

            <i class="bi bi-chevron-right promo-chev" aria-hidden="true"></i>
          </button>
        </li>
      </ul>

      <!-- Quiet disclosure, the pattern Actions uses for what it holds back: a
           secondary view you open, not a section competing with the list. -->
    </section>
    </Section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatEuro, formatNumber } from '@/i18n/format'
import Section from '@/components/ui/Section.vue'
import DateField from '@/components/ui/DateField.vue'
import Tooltip from '@/components/ui/Tooltip.vue'

import {
  after as afterOf, afterClass, archetype, archetypeClass, attentionFrom, breakEvenLine,
  during as duringOf, duringFromAttention,
  eur, fmtRange, mechanic as mechanicOf, NO_DISCOUNT_MECHANICS,
  salesRows, salesRowsNote,
  priorPromoPrompt, repeatAnswer, repeatClass, rollup,
} from '@/promotions/vocab'

/**
 * The promotions overview, with no idea where its data came from.
 *
 * Two surfaces mount it: the demo store, which hands it fixtures, and the live
 * portal, which hands it the API. Keeping one copy is the point — this page took a
 * long review to get right and a second copy would start drifting from the first the
 * day it was made.
 */
const props = defineProps({
  /** Promos, each with its `measurement` payload (coach/promo.measure_promo). */
  promos: { type: Array, default: () => [] },
  /** Product zones a promo can be attached to (GET /promos/zones/). */
  zones: { type: Array, default: () => [] },
  /** Which of those are counting lines: they measure everything but who stopped. */
  countsOnlyZones: { type: Array, default: () => [] },
  /** [{ key, label, hint }] for "how much of the zone do these items cover?" */
  coverageOptions: { type: Array, default: () => [] },
  /** What you said last time you promoted in the drafted zone, or null. */
  priorPromo: { type: Object, default: null },
  /** Live feedback for the drafted dates (GET /promos/readiness/), or null. */
  readiness: { type: Object, default: null },
  /** ISO date to treat as today. Defaults to the real one; the demo shifts it. */
  today: { type: String, default: () => new Date().toISOString().slice(0, 10) },
  /** Can this user log and change promos? Writes are admin-only in production. */
  canEdit: { type: Boolean, default: true },
  /** True on the demo store: nothing is persisted, and the page says so. */
  demo: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
})
const emit = defineEmits(['log', 'draft-change'])

const router = useRouter()
const { t, locale } = useI18n()
const isNl = () => locale.value === 'nl'
/** Thousands as the reader writes them: 1,234 / 1.234. */
const num = (v) => (isNl() ? formatNumber(v, { decimals: Number.isInteger(Number(v)) ? 0 : 2 })
  : Number(v).toLocaleString('en-GB'))
/** A per-item margin: €7.50 / € 7,50. */
const eur2 = (v) => (isNl() ? formatEuro(v, { decimals: 2 }) : `€${v.toFixed(2)}`)
/** A detectable-change bar exactly as the server sent it, with the reader's decimal mark. */
const rawPct = (v) => (isNl() ? String(v).replace('.', ',') : v)
const promos = computed(() => props.promos)
const zones = computed(() => props.zones)

/** `today` shifted by n days, as an ISO string — the page's only clock. */
function dayStr(n = 0) {
  const d = new Date(props.today + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() - n)
  return d.toISOString().slice(0, 10)
}
/**
 * The three stretches to ask for, with the exact days named — but only once they
 * have all passed. A promo logged in advance has nothing to look up yet.
 */
/**
 * Which figures can be filled in today — decided by the dates on the form, one row
 * at a time as each stretch finishes. Shared with the detail page so the ask and the
 * calculation can never drift apart.
 */
/**
 * TODAY IS THE REAL TODAY HERE, deliberately, even though the demo's own data is
 * anchored to a whole-week-shifted "today" up to six days in the past. The dates in
 * this form come off a calendar the person is looking at, so "has that stretch
 * finished" has to be answered against the actual date — otherwise a promo that
 * ended a month ago reports its after-weeks as "not over yet".
 */
const salesAsk = computed(() => salesRows(draft.value.start, draft.value.end))
const salesNote = computed(() => salesRowsNote(draft.value.start, draft.value.end))
/** Gross margin is the one number the store cannot read off a single report line:
 *  revenue minus what the goods cost. Shown per period so the subtraction is on
 *  screen rather than implied. */
const grossOf = (row) => {
  const r = draft.value.salesPeriods[row.key] || {}
  if (r.revenue == null || r.revenue === '') return null
  // Store-wide the gross is the revenue times one average margin, so the subtraction
  // shown here is the same arithmetic the engine will do — which is the point of
  // showing it at all.
  if (storeWide.value) {
    const pct = Number(draft.value.storeMarginPct)
    if (!pct || pct <= 0 || pct >= 100) return null
    return Math.round(r.revenue * (pct / 100))
  }
  if (r.cost == null || r.cost === '') return null
  return r.revenue - r.cost
}
/** Revenue in but no cost on a stretch that is over — the fallback is about to be
 *  used, so say so here rather than letting an estimate arrive unannounced. */
const costMissing = computed(() => !storeWide.value && (salesAsk.value || []).some((row) => {
  const r = draft.value.salesPeriods[row.key] || {}
  return !row.pending && r.revenue != null && r.revenue !== ''
    && (r.cost == null || r.cost === '')
}))

/**
 * THE LIFECYCLE IS THE NAVIGATION, as on the Actions tab. A promo runs, then waits on
 * its sales figures, then has a number, and you only ever work in one of those at a
 * time. The middle stage has no equivalent in Actions and is the one that earns its
 * own tab: everything is measured except the money, and three numbers off a till
 * report would finish it.
 */
/**
 * Always open on Running. What is on right now is what a manager wants first, and a
 * landing tab that moves with the data is a page that feels different every visit.
 * `?stage=` overrides it — which is also how the detail page sends you back to the
 * tab you came from.
 */
/** A euro only exists once somebody has given us the sales figures. */
const hasMoney = (p) => !unmeasurable(p) && p.measurement.margin?.confidence
  && p.measurement.margin.confidence !== 'na'

const stage = ref('running')

/**
 * Has the promo itself finished? Not the same as `complete`, which also waits out
 * the four weeks after. A promo that ended yesterday used to sit under "Running —
 * on now, or still to come" for another month, which is simply not true, and it hid
 * the during result for four weeks: the reading against the weeks before is finished
 * the day the promo ends. The tail is an addition to that answer, not a precondition
 * for having one.
 */
const hasEnded = (p) => !['planned', 'running'].includes(p.measurement.phase)

const byStage = computed(() => {
  const out = { running: [], waiting: [], measured: [] }
  for (const p of promos.value) {
    if (!hasEnded(p)) out.running.push(p)
    else if (hasMoney(p)) out.measured.push(p)
    else out.waiting.push(p)
  }
  return out
})

const stages = computed(() => ['running', 'waiting', 'measured'].map((key) => ({
  key,
  label: t(`promos.stages.${key}.label`),
  hint: t(`promos.stages.${key}.hint`),
  empty: t(`promos.stages.${key}.empty`),
  count: byStage.value[key].length,
})))
const currentStage = computed(() =>
  stages.value.find((st) => st.key === stage.value) || stages.value[0])

const qStage = String(router.currentRoute.value.query.stage || '')
if (['running', 'waiting', 'measured'].includes(qStage)) stage.value = qStage


/** Measured promos filter by the only question anyone asks of them. */
const resultFilter = ref('all')
const resultFilters = computed(() => {
  const m = byStage.value.measured
  return [
    { key: 'all', label: t('promos.list.filterAll'), count: m.length },
    { key: 'won', label: t('promos.list.filterWon'), count: m.filter((p) => p.measurement.margin.eur > 0).length },
    { key: 'lost', label: t('promos.list.filterLost'), count: m.filter((p) => p.measurement.margin.eur < 0).length },
  ]
})
const visibleMeasured = computed(() => byStage.value.measured.filter((p) => {
  if (resultFilter.value === 'won') return p.measurement.margin.eur > 0
  if (resultFilter.value === 'lost') return p.measurement.margin.eur < 0
  return true
}))
const visibleRows = computed(() => (stage.value === 'measured'
  ? visibleMeasured.value : byStage.value[stage.value]))

/**
 * The row's colour and category, mirroring the Actions tab's kind dots. What was DONE
 * is the useful category here — a price cut and a display move are different animals
 * and the store thinks of them that way.
 */
const KIND_OF = {
  pct_off: 'price', price_cut: 'price', bogo: 'price', bundle: 'price',
  display_move: 'display', demo: 'display', advertised: 'ads',
}
const kindOf = (p) => KIND_OF[p.Data.mechanic] || 'other'
/** What the row's euro covers — "margin" alone means nothing to a store manager. */
/** "1 day", not "1 days": a one-day promo is a real and common case. */
const dayS = (n) => t('promos.common.days', { n: num(n) }, Number(n))
const moneyScope = (p) => t('promos.scope.base', { days: dayS(p.measurement.windows.window.days) })
  // Said plainly rather than implied: on a promo that has ended but not settled,
  // this euro is the during figure alone, and calling it "plus the 4 weeks after"
  // would be promising a number we have not got.
  + (p.measurement.phase === 'complete'
    ? t('promos.scope.complete', { weeks: afterWeeks(p) })
    : t('promos.scope.pending', { weeks: afterWeeks(p) }))
  + t('promos.scope.setup', { eur: isNl()
    ? formatEuro(Number(p.measurement.margin.fixedCostEur),
      { decimals: Number.isInteger(Number(p.measurement.margin.fixedCostEur)) ? 0 : 2 })
    : `€${Number(p.measurement.margin.fixedCostEur).toLocaleString('en-GB')}` })
const kindLabel = (p) => mechanicOf(p.Data.mechanic).name

/** How far through its run a live promo is, for the progress bar. */
const ranDays = (p) => {
  const start = new Date(p.StartDate)
  const today = new Date(dayStr(0))
  return Math.min(runLength(p), Math.max(1, Math.round((today - start) / 86400000) + 1))
}
const runPct = (p) => Math.round((ranDays(p) / Math.max(1, runLength(p))) * 100)

const roll = computed(() => rollup(promos.value))
const stillRunning = computed(() =>
  promos.value.filter((p) => p.measurement.phase !== 'complete').length)
const minDate = computed(() => dayStr(365))

/**
 * Promo types. Price is only one of them: a feature display and an advertised
 * feature are standard trade promotions too, and they are the ones that cost you
 * nothing in margin — which is exactly what makes them worth measuring separately.
 */
const MECHANICS = computed(() => [
  'pct_off', 'bogo', 'bundle', 'price_cut', 'display_move', 'demo', 'advertised',
  'gift', 'store_event', 'loyalty', 'other',
].map((k) => ({ k, label: t(`promos.mechanics.${k}`) })))

/** The picker's value for a promo that is not attached to any shelf. */
const STORE_SCOPE = '__store__'
/**
 * "Dag van de klant" and its kind: the store itself is what was promoted, so there is
 * no zone, no coverage question and no item list — the money comes off the till and
 * the camera read is store footfall. See docs/reference/promo_measurement.md §15.0e.
 */
const storeWide = computed(() => draft.value.zone === STORE_SCOPE)

const showForm = ref(false)
const loggedNote = ref(false)
/** Enough to log: what it was called and when it ran. Everything else has a default
 *  or is genuinely optional, and blocking on the lot would teach the wrong lesson. */
const canLog = computed(() => !!draft.value.name.trim() && !!draft.value.start
  && !!draft.value.end && draft.value.end >= draft.value.start)
/**
 * The form's field names are the manager's; the API's are the schema's. The mapping
 * lives here, in the one place that knows both, so neither side has to carry the
 * other's vocabulary.
 *
 * Empty optional values are dropped rather than sent as blanks — the server stores
 * `Data` verbatim, and a blank is a value a later reader has to interpret.
 */
function logPayload() {
  const d = draft.value
  const items = d.items.filter((i) => (i.name || '').trim() || i.price > 0)
  const periods = {}
  for (const k of ['before', 'during', 'after']) {
    const row = d.salesPeriods[k] || {}
    const vals = {}
    for (const f of ['units', 'revenue', 'cost']) {
      if (row[f] !== null && row[f] !== undefined && row[f] !== '') vals[f] = row[f]
    }
    if (Object.keys(vals).length) periods[k] = vals
  }
  const isStore = d.zone === STORE_SCOPE
  return {
    name: d.name.trim(),
    scope: isStore ? 'store' : 'zone',
    zoneName: isStore ? undefined : d.zone,
    productScope: d.productScope.trim() || undefined,
    startDate: d.start,
    endDate: d.end,
    mechanic: d.mechanic,
    // Derived from the items, because the per-item fields are the ONLY discount the
    // form ever asks for. This used to send a draft default that no input touched and
    // the item table hid entirely store-wide, so a customer-day gift went into the
    // database as 30% off and the page said so.
    discountPct: (isStore || NO_DISCOUNT_MECHANICS.includes(d.mechanic) || !items.length)
      ? 0
      : Math.max(0, ...items.map((i) => Number(i.discountPct) || 0)),
    advertised: d.advertised,
    coverage: isStore ? undefined : d.coverage,
    items: isStore ? [] : items,
    salesPeriods: periods,
    salesCategoryPromo: d.catPromo.trim() || undefined,
    fixedCostEur: d.fixedCostEur || 0,
    storeMarginPct: isStore ? (Number(d.storeMarginPct) || undefined) : undefined,
    soldOut: d.soldOut,
    notes: d.notes.trim() || undefined,
  }
}

function onLog() {
  if (props.demo) {
    // the demo has nowhere to write to; say so rather than appearing to do nothing
    loggedNote.value = true
    return
  }
  emit('log', logPayload())
  showForm.value = false
}
const draft = ref({
  name: '',
  zone: '',
  productScope: '',
  // deliberately empty. Which sales figures we can ask for follows from the dates
  // and nothing else, so there is no default that could put the form in a state the
  // dates do not justify.
  start: '',
  end: '',
  mechanic: 'pct_off',
  // No input writes this: the discount the form asks for is per item, and the
  // payload derives this from them. Kept at 0 so nothing stale can be sent.
  discountPct: 0,
  catPromo: '',
  coverage: 'few_items',
  advertised: false,
  items: [{ name: '', price: null, marginPct: 45, discountPct: 30 }],
  salesPeriods: { before: {}, during: {}, after: {} },
  fixedCostEur: 0,
  // store-wide only; the zone path states its margin per item
  storeMarginPct: null,
  soldOut: false,
  notes: '',
})
/**
 * Arrived from "was that a promo?" — open the form with the zone and the dates
 * already in it.
 *
 * The question is only worth asking if answering it is one click. A notification
 * that says "tell us what this was" and then hands over an empty form, three months
 * after the event, is a chore nobody does: the dates are the part the manager cannot
 * remember, and they are the part we already know.
 */
const q = router.currentRoute.value.query
if (String(q.log || '') === '1') {
  const prefill = { zone: String(q.zone || ''), start: String(q.start || ''),
                    end: String(q.end || ''), scope: String(q.scope || '') }
  // A store-wide question is about the whole shop, so the scope picker starts there
  // rather than on an aisle the manager would then have to clear.
  if (prefill.scope === 'store') {
    draft.value.zone = STORE_SCOPE
  } else {
    // the zone has to be one actually on offer, or the select silently shows nothing
    watch(zones, (zs) => {
      if (prefill.zone && zs.includes(prefill.zone)) draft.value.zone = prefill.zone
    }, { immediate: true })
  }
  if (prefill.start) draft.value.start = prefill.start
  if (prefill.end) draft.value.end = prefill.end
  showForm.value = true
}

function addItem() {
  const last = draft.value.items[draft.value.items.length - 1] || {}
  // carry the rates down: the next line is usually at the same margin and discount
  draft.value.items.push({
    name: '', price: null,
    marginPct: last.marginPct ?? 45, discountPct: last.discountPct ?? 0,
  })
}
/**
 * The money per sale, averaged across the items — shown back on the form so an
 * average that looks wrong is caught here rather than in the result. Mirrors
 * coach/promo.item_economics: average the EUROS, never the percentages.
 */
const itemSummary = computed(() => {
  const rows = draft.value.items.filter((i) => i.price > 0 && i.marginPct > 0)
  if (!rows.length) return null
  const mean = (f) => rows.reduce((a, i) => a + f(i), 0) / rows.length
  const d = [...new Set(rows.map((i) => Math.round(i.discountPct || 0)))]
  return {
    items: rows.length,
    unitMarginNormal: mean((i) => i.price * (i.marginPct / 100)),
    unitMarginOnPromo: mean((i) => i.price * ((i.marginPct - (i.discountPct || 0)) / 100)),
    uniformDiscount: d.length === 1,
    discountPctMin: Math.min(...d),
    discountPctMax: Math.max(...d),
  }
})
/**
 * Picking the promo type sets the discount, rather than a footnote explaining the
 * arithmetic. A 2 for 1 takes half off each item; a display or a flyer takes nothing
 * off at all. Still editable — these are starting points, not rules.
 */
const MECHANIC_DISCOUNT = {
  bogo: 50, display_move: 0, demo: 0, advertised: 0,
  gift: 0, store_event: 0, loyalty: 0,
}
watch(() => draft.value.mechanic, (k) => {
  if (!(k in MECHANIC_DISCOUNT)) return
  for (const it of draft.value.items) it.discountPct = MECHANIC_DISCOUNT[k]
})

/** The sell-out question only makes sense once the promo has actually run. */
// an empty date string sorts before every real one, so the date has to exist before
// "has it run" means anything. Real today, for the same reason as `salesAsk`.
const alreadyRan = computed(() =>
  !!draft.value.end && draft.value.end < new Date().toISOString().slice(0, 10))
/** A euro only exists once somebody has given us the sales figures. */

const COVERAGE_OPTIONS = computed(() => props.coverageOptions)
const repeatOf = (p) => repeatAnswer(p.Data.outcome?.repeat)
/** Measured, and still waiting on the one thing the numbers cannot supply. */
const needsVerdict = (p) => p.measurement.phase === 'complete' && hasMoney(p)
  && !p.Data.outcome?.repeat
/** What you said about the last promo in the zone now selected in the form. */
const prior = computed(() => priorPromoPrompt(props.priorPromo))

// The zones arrive as a prop, which on the live portal means after a fetch, so the
// draft takes the first one when it turns up rather than at setup.
watch(zones, (zs) => {
  if (!draft.value.zone && zs.length) draft.value.zone = zs[0]
}, { immediate: true })
function openById(id) {
  router.push({ name: detailRoute.value, params: { promoId: String(id) } })
}
/** The verdict page's route differs between the demo store and the live portal. */
const detailRoute = computed(() =>
  (props.demo ? 'StoralyticDemoPromoDetail' : 'PromoVerdict'))
/**
   * Live feedback on the form. The parent owns it, because the demo computes it
   * locally and the portal asks the API — and it is recomputed from the draft, so
   * the draft is announced on every change rather than reached into from outside.
   */
const readiness = computed(() => props.readiness)
watch(() => ({
  zone: draft.value.zone,
  start: draft.value.start,
  end: draft.value.end,
  coverage: draft.value.coverage,
  economics: itemSummary.value,
  hasSales: !!draft.value.catPromo,
}), (d) => emit('draft-change', d), { deep: true, immediate: true })
/** The break-even stated in money, from the live form values. */
const breakEvenPreview = computed(() => {
  const e = itemSummary.value
  if (!e) return t('promos.list.addItemFirst')
  return breakEvenLine({
    breakEvenLift: e.unitMarginOnPromo > 0
      ? e.unitMarginNormal / e.unitMarginOnPromo - 1 : null,
    impossible: e.unitMarginOnPromo <= 0,
    discountPctMin: e.discountPctMin,
    discountPctMax: e.discountPctMax,
    uniformDiscount: e.uniformDiscount,
    unitMarginNormal: e.unitMarginNormal,
    unitMarginOnPromo: e.unitMarginOnPromo,
  })
})

const archKey = (p) => p.measurement.decay?.archetype
const arch = (p) => archetype(archKey(p))
/**
 * A promo the engine could not measure at all — a zone with no daily history, most
 * often one drawn after the promo ran. The payload is then `{error, phase}` with no
 * windows, decay or margin, and every accessor below has to survive it. The demo's
 * fixtures are always complete, so only the live API produces this.
 */
const unmeasurable = (p) => !!p.measurement?.error || !p.measurement?.decay

// Which ruler the verdict came off decides the words. With no sales figures the
// cameras are all we have, so the chips talk about people stopping, not selling.
const basisOf = (p) => p.measurement.decay?.basis || 'sales'
const duringOfP = (p) => (unmeasurable(p) ? null : basisOf(p) === 'traffic'
  // no sales figures: the chip pair is zone traffic, during and after, so the two
  // halves of the sentence are about the same thing
  ? duringFromAttention(p.measurement, p.Data.mechanic)
  : duringOf(p.measurement.decay.during, 'sales'))
const afterOfP = (p) => (unmeasurable(p)
  ? null : afterOf(p.measurement.decay.after, p.Data.mechanic, basisOf(p)))
const afterClsP = (p) => (unmeasurable(p)
  ? 'v-flat' : afterClass(p.measurement.decay.after, basisOf(p)))
const afterWeeks = (p) => p.measurement.windows?.post?.weeks || 4
/** What the cameras saw — free, and the only read available with no sales figures. */
const attention = (p) => attentionFrom(p.measurement, p.Data.mechanic)
/** Full length of the promo in days, so a running one can say "day 4 of 14". */
const runLength = (p) => Math.round(
  (new Date(p.EndDate) - new Date(p.StartDate)) / 86400000) + 1
/**
 * Neither the cameras nor the sales figures can yet see a move this size. Saying
 * "Sold more" here would assert exactly what our own detectability says we cannot
 * know — so the card says so instead.
 */
const tooEarly = (p) => {
  const d = p.measurement.detectability || {}
  return !d.camera?.isVerdict && !d.sales?.isVerdict
}
const TONE = { good: 'v-improved', bad: 'v-declined', flat: 'v-flat' }

// Through openById, so the route name comes from `detailRoute` — this used to name
// the demo route outright, and every promo opened from the live portal landed in the
// demo store.
const open = (p) => openById(p.PromoId)

/**
 * The card's one sentence. `withTotal: false` because the euro is already the big
 * number right above it — repeating it there made the card read twice.
 */

// ---- calendar strip geometry: a rolling 12 months ending today ----
const CAL_FROM = computed(() => new Date(dayStr(365) + 'T00:00:00Z'))
const CAL_TO = computed(() => new Date(dayStr(-14) + 'T00:00:00Z'))
const SPAN = computed(() => CAL_TO.value - CAL_FROM.value)

/** A promo on the store itself rather than on a shelf. */
const isStoreWide = (p) => p.Data.scope === 'store'

const STORE_ROW = 'Whole store'
/**
 * The rows of the year strip. A store-wide promo has no zone, so it gets a row of its
 * own at the top — leaving it off the timeline would hide the biggest thing the store
 * did all year.
 */
const zonesWithPromos = computed(() => [
  ...(promos.value.some(isStoreWide) ? [STORE_ROW] : []),
  ...zones.value.filter((z) => promos.value.some((p) => p.Data.zoneName === z)),
].filter(Boolean),
)
function promosFor(z) {
  if (z === STORE_ROW) return promos.value.filter(isStoreWide)
  return promos.value.filter((p) => p.Data.zoneName === z)
}
function bandStyle(p) {
  const s = new Date(p.StartDate + 'T00:00:00Z')
  const e = new Date(p.EndDate + 'T00:00:00Z')
  return {
    left: `${((s - CAL_FROM.value) / SPAN.value) * 100}%`,
    width: `${Math.max(4, ((e - s) / SPAN.value) * 100)}%`,
  }
}
/** Month ticks derived from the demo anchor, so they move with the rest of the demo. */
/**
 * THE ENDS ARE ALWAYS LABELLED, AND THEY CARRY THE YEAR.
 *
 * This is a ROLLING year, so it does not start in January — and a strip whose only
 * labels are bare month names puts "Jan" somewhere in the middle with nothing to say
 * what comes before it. The first and last ticks are therefore pinned to the two ends
 * of the track and written `Oct '25` … `Oct '26`, which answers both questions at a
 * glance: where it starts, and which of the two Octobers is which.
 *
 * Every month in between gets a tick too. They are thinned by CSS at narrower widths
 * (every second, then every third) and the two edges are never thinned, so the ends
 * are readable at any size.
 */
const axisTicks = computed(() => {
  // month names in the reader's language: "Oct" / "okt"
  const loc = isNl() ? 'nl' : 'en-GB'
  const yr = (d) => `${d.toLocaleDateString(loc, { month: 'short' })} '`
    + String(d.getUTCFullYear()).slice(2)
  const out = [{ key: 'start', edge: 'start', label: yr(CAL_FROM.value) }]

  const cur = new Date(Date.UTC(CAL_FROM.value.getUTCFullYear(), CAL_FROM.value.getUTCMonth() + 1, 1))
  while (cur < CAL_TO.value) {
    const left = ((cur - CAL_FROM.value) / SPAN.value) * 100
    // a tick too near either end sits underneath the pinned edge label, which is
    // exactly what made "Oct '25Nov" and "SeptOct '26" collide
    if (left >= 10 && left <= 85) {
      out.push({
        key: cur.toISOString(),
        label: cur.toLocaleDateString(loc, { month: 'short' }),
        left: `${left}%`,
      })
    }
    cur.setUTCMonth(cur.getUTCMonth() + 1)
  }

  out.push({ key: 'end', edge: 'end', label: yr(CAL_TO.value) })
  return out
})
</script>

<style scoped>
/* ===================================================================
   Tokens and shell, lifted from the Actions tab so the two pages are
   visibly the same product. Same ink/muted/line ramp, same accent, same
   1000px column, same radii. Anything promo-specific is built on top of
   these rather than beside them.
   =================================================================== */
/* CONTAINER QUERIES THROUGHOUT, not viewport ones.
   A 300px nav sidebar sits beside this page, so the column it actually gets is the
   window minus 340px — a tablet held in portrait at 820px leaves 500px here. Every
   viewport breakpoint on this page used to miss that case entirely and render
   seven-column tables and four-column rows into half a page. The thresholds below
   are therefore widths of THIS COLUMN, which is the only thing that matters. */
.pro {
  container: page / inline-size;
  --ink: #1d2b3a;
  --body: #425461;
  --muted: #6b7a87;
  --faint: #93a1ad;
  --line: #e6edf3;
  --line-strong: #d3dde7;
  --accent: var(--stora-accent, #4F7EB3);
  max-width: 1000px;
  --block: 880px;
}
/* What was DONE, as a category — the promo equivalent of the Actions kind dots. */
.pro .kind-price   { --k: #5b53a6; }
.pro .kind-display { --k: #0D9488; }
.pro .kind-ads     { --k: #b8892d; }
.pro .kind-other   { --k: #7d8c99; }

/* The page title is <Section>'s, as on every other page; this is the deck under it. */
.pro-top {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px 20px; flex-wrap: wrap;
}
.pro-sub { margin: 0; color: var(--muted); font-size: 0.92rem; }
.pro-intro {
  margin: 6px 0 0; max-width: 680px; color: var(--body); font-size: 0.9rem;
  line-height: 1.5;
}

/* The year strip and the running total, above the tabs because both answer a
   question that spans the whole lifecycle rather than one stage of it. */
/* Side by side only when there is genuinely room for both. A year of bands needs
   horizontal space, and sharing the row with the total starved it on any window
   under ~1000px — exactly the common laptop width. Below that the total goes above
   the strip and each gets the full column. */
.overview {
  margin: 18px 0 22px; padding: 14px 18px;
  background: #fff; border: 1px solid var(--line); border-radius: 14px;
}
.pro-earned {
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 12px;
  margin-top: 14px; padding-top: 13px; border-top: 1px solid var(--line);
}
.ov-cal { min-width: 0; }
.ov-head {
  margin-bottom: 9px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--muted);
}
.pro-earned-label {
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--muted); white-space: nowrap;
}
.pro-earned-label .bi { color: #c69a2b; }
.pro-earned-value {
  font-size: 1.5rem; font-weight: 800; color: #2c7a3f; line-height: 1.2;
  letter-spacing: -0.4px;
}
.pro-earned-value.is-neg { color: #b3372f; }
.pro-earned-note, .pro-earned-split {
  font-size: 0.76rem; color: var(--faint); white-space: nowrap;
}


/* Lifecycle tabs */
.stage-tabs {
  display: flex; align-items: stretch; gap: 4px; flex-wrap: wrap;
  margin-bottom: 22px; border-bottom: 1px solid var(--line);
}
.stage-tab {
  display: flex; align-items: center; gap: 10px; margin-bottom: -1px;
  padding: 10px 14px 12px; background: none; border: none;
  border-bottom: 2px solid transparent; color: var(--muted); font: inherit;
  cursor: pointer;
}
.stage-tab:hover { background: #f5f8fb; color: var(--ink); }
.stage-tab.active { border-bottom-color: var(--accent); color: var(--ink); }
.stage-tab:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.stage-count {
  min-width: 30px; height: 30px; padding: 0 9px; display: grid; place-items: center;
  border-radius: 999px; background: #eef2f6; color: var(--muted);
  font-size: 0.85rem; font-weight: 800;
}
.stage-tab.active .stage-count { background: var(--accent); color: #fff; }
.stage-text { display: flex; flex-direction: column; text-align: left; line-height: 1.2; }
.stage-label { font-size: 0.95rem; font-weight: 700; }
.stage-hint { font-size: 0.76rem; color: var(--faint); }
.stage-arrow { display: flex; align-items: center; color: #c8d1da; font-size: 0.8rem; }
/* One even row that never wraps, the same shape the Actions tab settled on: the
   three tabs share the width, the hints and arrows go, and the label is held on one
   line. "Waiting on figures" breaking onto two lines made the bar look broken. */
@container page (max-width: 560px) {
  .stage-tabs { flex-wrap: nowrap; gap: 0; }
  .stage-hint, .stage-arrow { display: none; }
  .stage-tab { flex: 1 1 0; min-width: 0; justify-content: center; gap: 7px; padding: 8px 4px 10px; }
  .stage-count { min-width: 24px; height: 24px; padding: 0 7px; font-size: 0.82rem; }
  .stage-label { font-size: 0.86rem; white-space: nowrap; }
}
/* Narrower still: the count sits above its name rather than beside it, which buys
   the label the whole width of its tab. */
@container page (max-width: 420px) {
  .stage-tab { flex-direction: column; gap: 3px; padding: 6px 2px 8px; }
  .stage-text { align-items: center; }
  .stage-label { font-size: 0.8rem; }
}
.stage-intro { margin: 0 0 16px; max-width: 680px; color: var(--body); font-size: 0.9rem; line-height: 1.5; }
.empty-state { padding: 28px 0; color: var(--muted); font-style: italic; }

/* Notice */
.notice {
  display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
  padding: 10px 14px; border-radius: 10px; background: #fdf7ec;
  border: 1px solid #f0e0c2; color: var(--body); font-size: 0.88rem;
}
.notice-ic { font-size: 1.1rem; color: #b8892d; }
.notice-body { flex: 1; min-width: 0; line-height: 1.45; }

/* Toolbar + filter pills */
.toolbar {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  flex-wrap: wrap; margin-bottom: 14px;
}
.filters { display: flex; flex-wrap: wrap; gap: 8px; }
.filter {
  display: inline-flex; align-items: center; gap: 7px; padding: 6px 12px;
  background: #fff; border: 1px solid var(--line); border-radius: 999px;
  color: var(--body); font: inherit; font-size: 0.84rem; font-weight: 600;
  cursor: pointer;
}
.filter:hover:not(:disabled) { border-color: var(--line-strong); }
.filter.active { background: var(--ink); border-color: var(--ink); color: #fff; }
.filter:disabled { opacity: 0.45; cursor: default; }
.filter:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.filter-n { color: var(--faint); font-weight: 700; }
.filter.active .filter-n { color: rgba(255, 255, 255, 0.7); }

.dot { flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; background: var(--k, var(--faint)); }

/* One meta line, the same one the Actions lists use */
.meta { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; font-size: 0.8rem; }
.meta-kind { color: var(--k); font-weight: 700; }
.meta-sep { color: #c8d1da; }
.meta-zone { color: var(--ink); font-weight: 700; }
.meta-date { color: var(--faint); }
.meta-date::before { content: '·'; margin-right: 6px; color: #c8d1da; }
.meta-tag {
  display: inline-flex; align-items: center; gap: 5px; padding: 1px 8px;
  border-radius: 999px; background: #f1f4f7; color: var(--muted);
  font-size: 0.72rem; font-weight: 700;
}
.meta-tag.tag-nomoney { background: #fdf7ec; color: #9a6b12; }
.meta-tag.tag-alarm { background: #fdeceb; color: #a8362c; }
.meta-tag.tag-ask { background: #eef4fa; color: #35618f; }

/* The list row */
.promo-list { display: grid; gap: 10px; margin: 0; padding: 0; list-style: none; }
.promo {
  overflow: hidden; background: #fff; border: 1px solid var(--line);
  border-radius: 14px; transition: border-color .15s ease, box-shadow .15s ease;
}
.promo:hover { border-color: var(--line-strong); box-shadow: 0 4px 16px rgba(13, 38, 76, .06); }
.promo-row {
  display: grid; grid-template-columns: minmax(0, 1fr) auto 170px 16px;
  align-items: center; gap: 20px; width: 100%; padding: 14px 18px;
  background: none; border: none; font: inherit; text-align: left; cursor: pointer;
}
.promo-row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.promo-id { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.promo-what {
  overflow: hidden; color: var(--body); font-size: 0.9rem; white-space: nowrap;
  text-overflow: ellipsis;
}
.promo-reading { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.promo-reading-label { color: var(--faint); font-size: 0.74rem; }
.promo-reading-value { color: var(--ink); font-size: 0.92rem; font-weight: 700; white-space: nowrap; }
.promo-reading-value.rd-up { color: #2c7a3f; }
.promo-reading-value.rd-down { color: #b3372f; }
.promo-reading-value.rd-pending { color: #9a6b12; font-weight: 600; }
.promo-status { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.promo-status-note { color: var(--faint); font-size: 0.74rem; }
.progress { display: block; width: 120px; height: 6px; overflow: hidden; border-radius: 999px; background: #eef2f6; }
.progress-fill { display: block; height: 100%; border-radius: 999px; background: var(--accent); }
.promo-chev { color: var(--faint); }
/* Below this the reading and the status cannot both sit beside the name without
   squeezing it to nothing, so they stack underneath it. */
@container page (max-width: 660px) {
  .promo-row { grid-template-columns: minmax(0, 1fr) 16px; gap: 6px 10px; }
  .promo-id { grid-column: 1; grid-row: 1; }
  .promo-chev { grid-column: 2; grid-row: 1; align-self: start; margin-top: 4px; }
  .promo-reading {
    grid-column: 1; flex-direction: row; align-items: baseline; gap: 8px;
    text-align: left;
  }
  .promo-status { grid-column: 1; flex-direction: row; align-items: center; gap: 9px; }
}
@container page (max-width: 420px) {
  .promo-row { padding: 12px 14px; }
  .promo-status { flex-wrap: wrap; }
  .progress { width: 86px; }
}

/* Buttons, the Actions set */
.btn-primary-sm {
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px;
  background: var(--accent); border: none; border-radius: 999px; color: #fff;
  font: inherit; font-size: 0.84rem; font-weight: 700; white-space: nowrap;
  cursor: pointer;
}
.btn-primary-sm:hover { filter: brightness(1.08); }

.promos-wrap { margin-left: 20px; padding-right: 20px; padding-bottom: 60px; --block: 880px; }
/* Phone: no gutter beside a sidebar that is now the hamburger menu (same rule as
   every other page's .page-gutter). */

.lead, .form-card, .readiness, .cal { max-width: var(--block); }

.lead { color: var(--stora-muted, #6b7a86);
  font-size: 14px; line-height: 1.55; margin: 0 0 18px;
}

/* a single column, for groups where the fields read as a sequence rather than a grid */
.form-stack { display: flex; flex-direction: column; gap: 14px; max-width: 420px; }

/* ---------- the three-period ask ---------- */
.sales-ask { margin-top: 16px; padding: 14px 16px; border-radius: 9px;
             background: #f9fcff; border: 1px solid #e2ecf7; }
.sales-ask-why { margin: 0 0 12px; font-size: 12.5px; line-height: 1.55;
                 color: var(--stora-muted, #6b7a86); }
.sales-ask-t { width: 100%; border-collapse: collapse; }
.sales-ask-t th { text-align: left; padding: 0 10px 7px 0; font-size: 10px;
                  font-weight: 800; text-transform: uppercase; letter-spacing: .05em;
                  color: #9aa7b2; }
.sales-ask-t th:not(:first-child) { width: 104px; }
.sales-ask-t th small { display: block; font-size: 9px; font-weight: 700;
                        letter-spacing: 0; text-transform: none; color: #b3bfc9; }
/* the derived column: no input, so it reads as an answer rather than a field */
.sales-ask-t th.col-gross { color: #38454f; }
.sales-ask-t td.col-gross { text-align: right; padding-right: 2px;
                            font-variant-numeric: tabular-nums; }
.gross-val { font-weight: 800; font-size: 13.5px; color: #2f6f4f; }
.gross-na { color: #c3ccd4; }
.sales-ask-note { margin: 10px 0 0; font-size: 12px; line-height: 1.6;
                  color: var(--stora-muted, #6b7a86); max-width: 62ch; }
.store-margin { display: block; margin: 12px 0 2px; }
.store-margin > span {
  display: block; margin-bottom: 5px;
  font-size: 12px; font-weight: 700; color: #6b7a87;
  text-transform: uppercase; letter-spacing: 0.03em;
}
.store-margin input {
  width: 120px; padding: 6px 9px; font-size: 13.5px;
  border: 1px solid var(--stora-border, #e6edf3); border-radius: 7px;
}
.sales-ask-warn {
  display: flex; gap: 8px; margin: 9px 0 0; padding: 9px 11px; border-radius: 8px;
  background: #fff8ec; border: 1px solid #f0dcb8; color: #6a5327;
  font-size: 12px; line-height: 1.6; max-width: 62ch;
}
.sales-ask-warn i { font-size: 13px; line-height: 1.5; }
.sales-ask-t td { padding: 0 10px 9px 0; vertical-align: middle; }
.sales-ask-t td b { font-size: 13.5px; color: #38454f; }
.sales-when { display: block; margin-top: 2px; font-size: 11.5px;
              color: var(--stora-muted, #6b7a86); }
.sales-ask-t input[disabled] { background: #f2f5f8; cursor: not-allowed; }
.sales-ask-t input { width: 100%; padding: 7px 9px; border-radius: 7px; font-size: 13.5px;
                     text-align: right; font-variant-numeric: tabular-nums; color: inherit;
                     border: 1px solid var(--stora-border, #e6edf3); background: #fff; }
.sales-later { margin: 14px 0 0; font-size: 12.5px; line-height: 1.55;
               color: var(--stora-muted, #6b7a86); }

/* ---------- the hand-typed sales feed ---------- */
.sales { gap: 7px; }
.sales-state {
  display: flex; align-items: flex-start; gap: 7px;
  margin: 3px 0 0; padding: 9px 11px; border-radius: 8px;
  font-size: 12.5px; line-height: 1.5;
}
.sales-state i { font-size: 13px; margin-top: 1px; }
.sales-ok { background: #f3fbf5; border: 1px solid #d9f1df; color: #2c7a3f; }
.sales-warn { background: #fff7e6; border: 1px solid #ffe7bf; color: #b66a00; }
.sales-none { background: var(--stora-bg, #f7fafc); border: 1px solid var(--stora-border, #e6edf3); color: #5b6a76; }
.sales-add-link {
  align-self: flex-start; margin-top: 2px; padding: 0; background: none; border: none;
  cursor: pointer; font-size: 12.5px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
}
.sales-add-link:hover { text-decoration: underline; }
.sales-inline {
  margin-top: 8px; padding: 13px 15px; border-radius: 9px;
  background: #f9fcff; border: 1px solid #e2ecf7;
}
.sales-why {
  margin: 0 0 11px; font-size: 12.5px; line-height: 1.55; color: var(--stora-muted, #6b7a86);
}
.sales-save { margin-top: 11px; }

/* ---------- the item table ---------- */
.items { width: 100%; border-collapse: collapse; }
.items th {
  text-align: left; padding: 0 10px 7px 0; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .05em; color: #9aa7b2; white-space: nowrap;
}
.items th:not(:first-child) { width: 96px; }
.items th:last-child { width: 28px; }
.items th small { display: block; font-size: 9px; font-weight: 700; letter-spacing: 0;
                  text-transform: none; color: #b3bfc9; }
.items input[disabled] { background: #f2f5f8; cursor: not-allowed; }
.items td { padding: 0 10px 8px 0; vertical-align: middle; }
.items input {
  width: 100%; padding: 7px 9px; border-radius: 7px; font-size: 13.5px; color: inherit;
  border: 1px solid var(--stora-border, #e6edf3); background: var(--stora-card, #fff);
}
.items input[type="number"] { text-align: right; font-variant-numeric: tabular-nums; }
.row-x {
  width: 24px; height: 24px; border-radius: 6px; cursor: pointer; line-height: 1;
  border: 1px solid var(--stora-border, #e6edf3); background: none;
  color: var(--stora-muted, #6b7a86); font-size: 15px;
}
.row-x:hover { border-color: #e0b4b7; color: #b0464b; }
.row-add {
  margin-top: 2px; padding: 0; background: none; border: none; cursor: pointer;
  font-size: 12.5px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
}
.row-add:hover { text-decoration: underline; }
.items-sum {
  margin: 14px 0 0; padding: 11px 13px; border-radius: 8px;
  background: var(--stora-bg, #f7fafc); border: 1px solid var(--stora-border, #e6edf3);
  font-size: 13px; line-height: 1.6; color: #38454f;
}
.items-sum b { font-weight: 800; }
.items-note { display: block; margin-top: 3px; font-size: 12px; color: var(--stora-muted, #6b7a86); }
.items-note-good { color: #2f6f4f; }

@container page (max-width: 620px) {
  .items, .items tbody, .items tr, .items td { display: block; width: 100%; }
  .items thead { display: none; }
  .items tr { padding-bottom: 12px; }
  .items td { padding: 0 0 6px; }
  /* with the heading row gone, five number boxes in a column are unreadable
     without their names, so each cell carries its own */
  .items td[data-label]::before, .sales-ask-t td[data-label]::before {
    content: attr(data-label); display: block; margin-bottom: 3px;
    font-size: 10px; font-weight: 800; text-transform: uppercase;
    letter-spacing: .05em; color: #9aa7b2;
  }
  .sales-ask-t, .sales-ask-t tbody, .sales-ask-t tr, .sales-ask-t td {
    display: block; width: 100%;
  }
  .sales-ask-t thead { display: none; }
  .sales-ask-t tr { padding: 0 0 14px; }
  .sales-ask-t td { padding: 0 0 7px; }
  .sales-ask-t td.col-gross { text-align: left; }
}

/* ---------- form: four short groups, not one wall of fields ---------- */
.fgroup { padding-top: 18px; }
.fgroup:first-child { padding-top: 0; }
.fgroup + .fgroup { border-top: 1px solid var(--stora-border, #e6edf3); margin-top: 18px; }
.fgroup-head {
  margin-bottom: 13px; font-size: 10.5px; font-weight: 800; text-transform: uppercase;
  letter-spacing: .07em; color: #9aa7b2;
}
/* The box and its label are one line; the hint sits under them, indented to the
   label. Relying on flex-wrap put the hint on line one and the label on line two,
   because the hint is the longest child — structure, not wrapping rules. */
.chk { gap: 4px; padding: 2px 0; }
.chk-line { display: flex; align-items: center; gap: 9px; }
.chk-line input[type="checkbox"] {
  width: 15px; height: 15px; margin: 0; flex: none; cursor: pointer;
}
/* same weight, size and colour as every other field label */
.chk-line span {
  font-size: 12.5px; font-weight: 600; color: var(--stora-muted, #6b7a86); line-height: 1.4;
}
.chk .hint { margin-left: 24px; }

/* ---------- form ---------- */
.form-card {
  padding: 18px; border-radius: 12px;
  border: 1px solid var(--stora-border, #e6edf3); background: var(--stora-card, #fff);
}
.form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; }
.fld { display: flex; flex-direction: column; gap: 5px; font-size: 12.5px; }
.fld-wide { grid-column: span 2; }
.fld > span { color: var(--stora-muted, #6b7a86); font-weight: 600; }
.fld input, .fld select {
  padding: 7px 9px; border-radius: 7px; font-size: 13.5px; color: inherit;
  border: 1px solid var(--stora-border, #e6edf3); background: #fff;
}
.hint { font-size: 11px; color: var(--stora-muted, #6b7a86); font-weight: 400; }

/* ---------- optional extras ---------- */
.extra { margin-top: 16px; }
.extra summary {
  cursor: pointer; font-size: 12.5px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
}
.extra summary span { font-weight: 400; color: var(--stora-muted, #6b7a86); }
.extra-body {
  margin-top: 10px; padding: 13px 15px; border-radius: 9px;
  background: var(--stora-bg, #f7fafc); border: 1px solid var(--stora-border, #e6edf3);
}
.extra-body p {
  margin: 0 0 11px; font-size: 12.5px; line-height: 1.55;
  color: var(--stora-muted, #6b7a86);
}
.rd-note { margin: 12px 0 0; font-size: 12.5px; line-height: 1.5; color: #5b6a76; }

/* ---------- readiness ---------- */
.readiness {
  margin-top: 18px; padding: 16px; border-radius: 10px;
  background: var(--stora-bg, #f7fafc); border: 1px solid var(--stora-border, #e6edf3);
}
.rd-head { font-weight: 700; font-size: 13px; margin-bottom: 12px; }
.rd-head i { margin-right: 6px; color: var(--stora-accent, #4F7EB3); }
.rd-list { margin: 0; display: flex; flex-direction: column; gap: 9px; }
.rd-row { display: grid; grid-template-columns: 140px 1fr; gap: 12px; align-items: baseline; }
.rd-waiting {
  margin: 18px 0 0; font-size: 12.5px; font-style: italic;
  color: var(--stora-muted, #6b7a86);
}
.rd-row dt { font-size: 12px; color: var(--stora-muted, #6b7a86); font-weight: 600; }
.rd-row dd { margin: 0; font-size: 13px; line-height: 1.5; }
.rd-mode { color: var(--stora-muted, #6b7a86); }
.rd-risk { margin: 12px 0 0; font-size: 12.5px; line-height: 1.5; color: #b66a00; }
.danger { color: #b0464b; }

.form-actions {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 16px;
}
.form-hint { font-size: 12px; color: var(--stora-muted, #6b7a86); }
.demo-note {
  display: flex; gap: 8px; margin: 12px 0 0; padding: 9px 11px; border-radius: 8px;
  background: #eef5fa; border: 1px solid #d6e5f0; color: #35576b;
  font-size: 12.5px; line-height: 1.6;
}
.btn-primary, .btn-ghost {
  padding: 7px 15px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.btn-primary { background: var(--stora-accent, #4F7EB3); color: #fff; border: none; }
.btn-primary[disabled] { opacity: .45; cursor: not-allowed; }
.btn-ghost { background: transparent; border: 1px solid var(--stora-border, #e6edf3); color: inherit; }

/* ---------- what you said last time ---------- */
.prior {
  display: grid; grid-template-columns: 20px 1fr; gap: 11px; align-items: start;
  margin-top: 18px; padding: 13px 16px; border-radius: 10px;
  border: 1px solid var(--stora-border, #e6edf3); border-left: 3px solid #b6c1cb;
  background: var(--stora-bg, #f7fafc);
}
.prior.v-improved { border-left-color: #2c7a3f; background: #f3fbf5; }
.prior.v-declined { border-left-color: #b0464b; background: #fdf3f3; }
.prior > i { font-size: 15px; color: var(--stora-muted, #6b7a86); line-height: 1.5; }
.prior-line { margin: 0; font-size: 13.5px; line-height: 1.55; color: #38454f; }
.prior-note {
  margin: 5px 0 0; font-size: 13px; line-height: 1.55; font-style: italic;
  color: var(--stora-muted, #6b7a86);
}
.prior-link {
  margin-top: 7px; padding: 0; background: none; border: none; cursor: pointer;
  font-size: 12.5px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
}
.prior-link:hover { text-decoration: underline; }
.pc-num.early { color: #b66a00; }
.pc-num.neg { color: #b0464b; }
.pc-num.pos { color: #2c7a3f; }

/* ---------- the ONE result palette, shared with GuidanceTab ---------- */
.verdict-chip {
  font-size: 0.72rem; font-weight: 800; padding: 3px 10px;
  border-radius: 999px; white-space: nowrap;
}
.verdict-chip.arch { font-size: 0.75rem; }
.v-early { background: #fff7e6; color: #b66a00; }
.v-improved { background: #f3fbf5; color: #2c7a3f; }
.v-declined { background: #fdf3f3; color: #b0464b; }
.v-flat { background: #f2f5f8; color: #6c7a86; }

/* ---------- calendar ----------
   The bands ARE the pills: same class set, same soft tint. Saturated colour in this
   product means "which zone" (the zone-type palette); soft tint means "how did it
   go". Painting the bands in saturated red/amber/green made them read as zone
   identity instead, and broke the link to the pills in the list. */
/* NO HORIZONTAL SCROLL, at any width. The strip used to hold a 460px minimum and
   scroll sideways inside its own box, which put a scrollbar in the middle of the
   page the moment the window narrowed. The bands are positioned in percentages, so
   the timeline compresses perfectly well on its own; what has to give at narrow
   widths is the furniture around it — the zone column moves above its track, the
   band labels drop (they clip into fragments long before they become unreadable)
   and the month axis thins out. */
/* CONTAINER queries, not viewport ones. What decides whether twelve month labels
   fit is the width of THIS STRIP — which is set by the nav sidebar and the column
   cap, not by the window. On a wide screen with the sidebar open the strip is about
   300px, so every viewport breakpoint missed and the labels collided anyway. */
.cal { position: relative; container: cal / inline-size; }
.cal-row { display: grid; grid-template-columns: minmax(0, 150px) 1fr; align-items: center; gap: 12px; margin-bottom: 9px; }
.cal-zone {
  font-size: 12.5px; font-weight: 600; color: var(--stora-muted, #6b7a86);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cal-track {
  position: relative; height: 28px; border-radius: 6px; min-width: 0;
  background: var(--stora-bg, #f7fafc);
  border: 1px solid var(--stora-border, #e6edf3);
}
.cal-band {
  position: absolute; top: 3px; bottom: 3px; border-radius: 5px; cursor: pointer;
  display: flex; align-items: center; padding: 0 8px; overflow: hidden;
  border: 1px solid currentColor;
}
.cal-axis-row { margin-top: 2px; margin-bottom: 0; align-items: start; }
.cal-axis { position: relative; height: 16px; min-width: 0; }
.cal-axis span {
  position: absolute; font-size: 11px; color: var(--stora-muted, #6b7a86);
  transform: translateX(-50%); white-space: nowrap;
}
/* The two ends are pinned to the ends of the track, never centred on a tick and
   never thinned away: they are the only thing that says which year you are in. */
.axis-edge { transform: none; font-weight: 700; color: #4b5a66; }
.axis-start { left: 0; }
.axis-end { right: 0; }
/* Thinned on the strip's own width. The two edges are never in these sets. */
@container cal (max-width: 620px) {
  .cal-axis .axis-mid:nth-child(even) { display: none; }
}
@container cal (max-width: 420px) {
  .cal-axis .axis-mid:not(:nth-child(3n + 2)) { display: none; }
}
/* And the zone name moves above its own track when the strip itself is narrow,
   whatever the window is doing. */
@container cal (max-width: 380px) {
  .cal-row { grid-template-columns: 1fr; gap: 3px; margin-bottom: 12px; }
  .cal-axis-row > :first-child { display: none; }
}

/* ---------- responsive ---------- */

@container page (max-width: 620px) {
  .fld-wide { grid-column: span 1; }
  .rd-row { grid-template-columns: 1fr; gap: 2px; }
  .cal-zone { font-size: 11.5px; }
  .pro-top { gap: 10px; }
  .pro-top .btn-primary-sm { width: 100%; justify-content: center; }
}

/* Once the sidebar overlays instead of offsetting, the app shell owns the gutter —
   so this page drops its own and sits exactly where every other page does. Adding a
   gutter here put the promo pages 14px further from the edge than their neighbours. */
@media (max-width: 768px) {
  .promos-wrap { margin-left: 0; padding-right: 0; }
}
@container page (max-width: 480px) {
  .form-card, .readiness, .overview { padding: 14px; }
}
</style>
